import { useState } from 'react';
import { Modal, Button, Input, Select, Checkbox } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import type { Account, AccountType, CreateAccountInput, UpdateAccountInput } from '@perfin/shared';
import { ACCOUNT_TYPES } from '@perfin/shared';

interface AccountFormProps {
  account: Account | null;
  onClose: () => void;
}

export function AccountForm({ account, onClose }: AccountFormProps) {
  const { entities, addAccount, editAccount } = useSettingsStore();
  const toast = useToast();

  const [entityId, setEntityId] = useState(account?.entityId || '');
  const [accountName, setAccountName] = useState(account?.accountName || '');
  const [accountNumber, setAccountNumber] = useState(account?.accountNumber || '');
  const [type, setType] = useState<AccountType>(account?.type || 'bank');
  const [isDefault, setIsDefault] = useState(account?.isDefault || false);
  const [saving, setSaving] = useState(false);

  const isEditing = !!account;

  // Get entity options
  const entityOptions = [
    { value: '', label: 'Select entity...' },
    ...entities.filter((e) => e.isActive).map((entity) => ({
      value: entity.id,
      label: entity.name,
    })),
  ];

  const accountTypeOptions = ACCOUNT_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!entityId) {
      toast.error('Please select an entity');
      return;
    }

    if (!accountName.trim()) {
      toast.error('Account name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        const input: UpdateAccountInput = {
          entityId,
          accountName: accountName.trim(),
          accountNumber: accountNumber.trim() || undefined,
          type,
        };
        await editAccount(account.id, input);
        toast.success('Account updated');
      } else {
        const input: CreateAccountInput = {
          entityId,
          accountName: accountName.trim(),
          accountNumber: accountNumber.trim() || undefined,
          type,
          isDefault,
        };
        await addAccount(input);
        toast.success('Account created');
      }
      onClose();
    } catch {
      toast.error(isEditing ? 'Failed to update account' : 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? 'Edit Account' : 'Add Account'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Entity Selection */}
        <Select
          label="Entity"
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          options={entityOptions}
        />

        {/* Account Name */}
        <Input
          label="Account Name"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          placeholder="e.g., Checking, Savings, Credit Card 1"
        />

        {/* Account Number (optional) */}
        <Input
          label="Account Number (optional)"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          placeholder="e.g., 1234567890"
        />
        <p className="text-xs text-gray-500 -mt-4">
          Only the last 4 digits will be displayed for security
        </p>

        {/* Account Type */}
        <Select
          label="Account Type"
          value={type}
          onChange={(e) => setType(e.target.value as AccountType)}
          options={accountTypeOptions}
        />

        {/* Set as Default (only for new accounts) */}
        {!isEditing && (
          <div>
            <Checkbox
              label="Set as default account"
              checked={isDefault}
              onChange={() => setIsDefault(!isDefault)}
            />
            <p className="mt-1 ml-6 text-xs text-gray-500">
              Default account is pre-selected when adding transactions
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {isEditing ? 'Save Changes' : 'Add Account'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
